import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SliderCaptcha } from "@/components/auth/SliderCaptcha";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { useHoneypot } from "@/hooks/useHoneypot";
import { useTimeProtection } from "@/hooks/useTimeProtection";

const Login = () => {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const isMaintenanceMode = searchParams.get('mode') === 'maintenance';
  const isComingSoonMode = searchParams.get('mode') === 'coming-soon';
  const hideRegisterLink = isMaintenanceMode || isComingSoonMode;
  const [isLoading, setIsLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const { isBot, honeypotProps } = useHoneypot();
  const { checkTiming } = useTimeProtection();

  const formSchema = z.object({
    email: z.string().email({ message: t("auth.emailInvalid") }),
    password: z.string().min(1, { message: t("auth.passwordRequired") }),
    rememberMe: z.boolean().optional(),
  });

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  const rememberMe = watch("rememberMe");

  // Get returnUrl from query params
  const returnUrl = searchParams.get('returnUrl');

  const onSubmit = async (data: FormData) => {
    // Anti-spam checks
    if (isBot) {
      // Blocco silenzioso per i bot
      console.warn("Bot detected via honeypot");
      return;
    }

    if (!captchaVerified) {
      toast({ title: t("auth.errors.captchaRequired"), variant: "error" });
      return;
    }

    const timingCheck = checkTiming();
    if (!timingCheck.isValid) {
      if (timingCheck.reason === "too_fast") {
        toast({ title: t("auth.errors.tooFast"), variant: "error" });
        return;
      }
    }

    setIsLoading(true);
    console.log("[Login] Attempting login for:", data.email);
    
    try {
      // Pass returnUrl (decoded) to signIn
      const decodedReturnUrl = returnUrl ? decodeURIComponent(returnUrl) : undefined;
      const { error } = await signIn(data.email, data.password, decodedReturnUrl);

      if (error) {
        console.error("[Login] Error:", error);
        if (error.message.includes("Invalid") || error.message.includes("invalid")) {
          toast({ title: "Email o password non corretti. Verifica le credenziali e riprova.", variant: "error" });
        } else if (error.message.includes("Email not confirmed")) {
          toast({ title: "Email non confermata. Controlla la tua casella di posta.", variant: "error" });
        } else {
          toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
        }
      } else {
        console.log("[Login] Success!");
        toast({ title: t("auth.signInSuccess"), variant: "success" });
      }
    } catch (error: any) {
      console.error("[Login] Unexpected error:", error);
      toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <BrutalCard className="w-full max-w-md">
        <div className="text-center mb-8">
          <AuthLogo />
          <h1 className="font-heading font-bold text-3xl text-dark mb-2">
            {t("auth.signIn")}
          </h1>
          <p className="text-muted-foreground font-body">
            {t("common.tagline")}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="email" className="font-heading font-bold uppercase text-xs mb-2 block">
              {t("auth.email")}
            </Label>
            <BrutalInput
              id="email"
              type="email"
              {...register("email")}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="font-heading font-bold uppercase text-xs mb-2 block">
              {t("auth.password")}
            </Label>
            <BrutalInput
              id="password"
              type="password"
              {...register("password")}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Honeypot field - nascosto per gli umani, visibile per i bot */}
          <input
            type="email"
            name="email_confirm"
            {...honeypotProps}
          />

          {/* Slider CAPTCHA */}
          <SliderCaptcha
            onVerify={setCaptchaVerified}
            onReset={() => setCaptchaVerified(false)}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setValue("rememberMe", checked as boolean)}
                disabled={isLoading}
              />
              <Label htmlFor="remember" className="text-sm font-body cursor-pointer">
                {t("auth.rememberMe")}
              </Label>
            </div>

            <Link
              to="/auth/forgot-password"
              className="text-sm font-body text-primary hover:underline font-bold"
            >
              {t("auth.forgotPassword")}
            </Link>
          </div>

          <BrutalButton
            type="submit"
            className="w-full"
            disabled={isLoading || !captchaVerified}
          >
            {isLoading ? t("auth.signingIn") : t("auth.signIn")}
          </BrutalButton>

          {!hideRegisterLink && (
            <p className="text-center text-sm font-body text-muted-foreground">
              {t("auth.dontHaveAccount")}{" "}
              <Link to="/auth/register" className="text-primary font-bold hover:underline">
                {t("auth.signUp")}
              </Link>
            </p>
          )}
        </form>
      </BrutalCard>
    </div>
  );
};

export default Login;
